<template>
    <div class="field-timer-process-modal">
        <TimerModal
            data-qa="field-timer-modal"
            :title="$t('funnel.edit.timer.field.title.setup')"
            :timer-tip-link="$t('funnel.edit.timer.field.proTipUrl')"
            :timer-tip-text="$t('funnel.edit.timer.field.proTip')"
            @close-modal="closeModal"
            @update-config="updateConfig"
        >
            <template #timerSummary>
                <!-- eslint-disable vue/no-v-html -->
                <span v-html="summaryText" />
                <!-- eslint-enable vue/no-v-html -->
            </template>
            <template #timerParameters>
                <div class="progress-slider">
                    <div v-if="validationErrorMessages !== null" class="errorMessage">
                        <DsInlineAlert leading-icon type="critical">
                            {{ validationErrorMessages }}
                        </DsInlineAlert>
                    </div>
                    <div class="field-timer-formLabel top-border">
                        {{ $t('funnel.edit.timer.waitUntil') }}
                    </div>
                    <div class="top-border-triple-wrapper">
                        <div class="formSpacer">
                            <DsInputField
                                v-model="waitDelay"
                                data-qa="field-timer-wait-delay"
                                type="number"
                                :readonly="hasNoDelay"
                                :step="1"
                                :min="waitDelayRange.minWaitDelay"
                                :max="waitDelayRange.maxWaitDelay"
                            />
                        </div>
                        <div class="formSpacer">
                            <DsMultiselect
                                v-model="waitDelayType"
                                searchable
                                data-qa="field-timer-wait-delay-type"
                                bind-value-only
                                label-prop="label"
                                value-prop="id"
                                :options="waitDelayTypes"
                            />
                        </div>
                        <div class="formSpacer">
                            <DsMultiselect
                                v-model="waitDelayModifierType"
                                data-qa="field-timer-wait-delay-modifier-type"
                                :readonly="hasNoDelay"
                                bind-value-only
                                label-prop="label"
                                value-prop="id"
                                :options="waitDelayModifierOptions"
                            />
                        </div>
                    </div>
                    <div class="field-timer-formLabel top-border">
                        {{ $t('funnel.edit.timer.field.contactField') }}
                    </div>
                    <div class="top-border-double-wrapper">
                        <div class="formSpacer">
                            <DsMultiselect
                                v-model="contactField"
                                class="contact-field-multiselect"
                                data-qa="field-timer-contact-field"
                                bind-value-only
                                searchable
                                label-prop="label"
                                value-prop="value"
                                :options="contactFields"
                            />
                        </div>
                        <div class="formSpacer">
                            <DsMultiselect
                                v-model="yearType"
                                data-qa="field-timer-year-type"
                                bind-value-only
                                label-prop="label"
                                value-prop="id"
                                :options="yearOptions"
                            />
                        </div>
                    </div>
                    <div class="top-border">
                        <DsRadioGroup
                            v-model="timeOfDayType"
                            class="timer-horizontal-radio"
                            data-qa="field-timer-time-of-day-type"
                            :options="timeOfDayOptions"
                        />
                        <div class="field-timer-formLabel">
                            <div v-if="isDateTimeCustomField">
                                {{ $t('funnel.edit.timer.field.timeSpecified') }}
                            </div>
                        </div>
                        <DsDatePicker
                            v-if="timeOfDayType !== timeDayTypesCons.ANY && !isDateTimeCustomField"
                            v-model="timeAsString"
                            time-only
                            :force-invalid="invalidStartTime"
                            :minute-increment="minuteIncrement"
                            :allow-input="false"
                            class="small-date-picker"
                        >
                            <template #error>
                                <span v-if="isMounted && invalidStartTime" data-qa="invalid-start-time">
                                    {{ $t('funnel.edit.timer.invalid') }}
                                </span>
                            </template>
                        </DsDatePicker>
                        <DsCheckbox v-model="useContactTimeZone">
                            {{ $t('funnel.edit.timer.useContactTimeZoneWhenAvailable') }}
                        </DsCheckbox>
                    </div>
                </div>
            </template>
        </TimerModal>
    </div>
</template>

<script>
import TimerModal from '../timerModal/TimerModal.vue';
import {
    loadDateAndDateTimeFieldMap,
    loadDateTimeFieldMap,
} from '../timers.api';
import {
    cleanUpTimeFields,
    createTime,
    isAfterOrEqualToNext,
    isBeforeOrEqualToPrevious,
    isValidMinute,
    TIME_DAY_TYPES,
} from '../timer.utils';
import {
    findIncomingCells,
    findOutgoingCells,
} from '../../../../utils/cell/automation-edge-utils';
import { TIMERS, TIMERS_TIME_INCREMENT } from '../../../../utils/editor.constants';

const WAIT_DELAY_CONSTANTS = {
    MINUTE: 'Minute',
    HOUR: 'Hour',
    DAY: 'Day',
    WEEKDAY: 'WeekDay',
    WEEK: 'Week',
    MONTH: 'Month',
    YEAR: 'Year',
    NO_DELAY: 'NoDelay',
    USE_YEAR: 'InField',
};

const WAIT_DELAY_RANGES = {
    [WAIT_DELAY_CONSTANTS.MINUTE]: [1, 1440],
    [WAIT_DELAY_CONSTANTS.HOUR]: [1, 168],
    [WAIT_DELAY_CONSTANTS.DAY]: [1, 365],
    [WAIT_DELAY_CONSTANTS.WEEKDAY]: [1, 365],
    [WAIT_DELAY_CONSTANTS.WEEK]: [1, 52],
    [WAIT_DELAY_CONSTANTS.MONTH]: [1, 60],
    [WAIT_DELAY_CONSTANTS.YEAR]: [1, 25],
    [WAIT_DELAY_CONSTANTS.NO_DELAY]: [0, 0],
    [WAIT_DELAY_CONSTANTS.USE_YEAR]: [0, 0],
};

const WAIT_DELAY_MODIFIER_CONSTANTS = { BEFORE: 'Before', AFTER: 'After' };
const YEAR_CONSTANTS = { NEXT: 'Next', INFIELD: 'InField' };

export default {
    WAIT_DELAY_CONSTANTS,
    WAIT_DELAY_MODIFIER_CONSTANTS,
    WAIT_DELAY_RANGES,
    YEAR_CONSTANTS,
    components: {
        TimerModal,
    },


    props: {
        cells: {
            type: Array,
            default: () => ([]),
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    errorMessages: null,
                    waitDelay: null,
                    waitDelayType: null,
                    waitDelayModifierType: null,
                    contactField: null,
                    yearType: null,
                    startHour: null,
                    startTime: null,
                    startMinute: null,
                    startAmPm: null,
                    useContactTimeZone: null,
                    timeOfDayType: null,
                },
            }),
        },
        appInfo: {
            type: Object,
            required: true,
        },
    },

    emits: ['close-modal', 'update-cell-value'],

    data() {
        return {
            isMounted: false,
            timeDayTypesCons: TIME_DAY_TYPES,
            minuteIncrement: Number(TIMERS_TIME_INCREMENT),
            contactFields: [],
            dateTimeContactFields: [],
            waitDelay: this.cell?.value?.waitDelay || 3,
            waitDelayType: this.cell?.value?.waitDelayType || WAIT_DELAY_CONSTANTS.DAY,
            waitDelayModifierType: this.cell?.value?.waitDelayModifierType || WAIT_DELAY_MODIFIER_CONSTANTS.BEFORE,
            contactField: this.cell?.value?.contactField,
            yearType: this.cell?.value?.yearType || YEAR_CONSTANTS.NEXT,
            startHour: this.cell?.value?.startHour || '08',
            startMinute: this.cell?.value?.startMinute || '00',
            startAmPm: this.cell?.value?.startAmPm || 'AM',
            useContactTimeZone: this.cell?.value?.useContactTimeZone === '1' || this.cell?.value?.useContactTimeZone === 1,
            timeOfDayType: this.cell?.value?.timeOfDayType ? this.cell?.value?.timeOfDayType : TIME_DAY_TYPES.AT,
            validationErrorMessages: null,
        };
    },

    computed: {
        invalidStartTime() {
            return this.timeOfDayType === this.timeDayTypesCons.AT && !isValidMinute(this.startMinute);
        },
        timeAsString: {
            set(startTime) {
                const hour = parseInt(startTime.substring(0, 2), 10);
                const minute = parseInt(startTime.substring(2, 4), 10);
                const hourToSet = (hour === 0 ? 12 : hour > 12 ? hour - 12 : hour);

                this.startHour = hourToSet.toString();
                this.startMinute = minute.toString();
                this.startAmPm = hour <= 11 ? 'AM' : 'PM';
            },
            get() {
                let hour = parseInt(this.startHour, 10);

                hour = this.startAmPm === 'PM' && hour < 12 ? hour + 12 : this.startAmPm === 'AM' && hour === 12 ? hour - 12 : hour;
                hour = `${hour}`.padStart(2, '0');
                const minute = `${this.startMinute}`.padStart(2, 0);

                return `${hour}${minute}`;
            },
        },

        waitDelayRange() {
            const range = WAIT_DELAY_RANGES[this.waitDelayType];

            return { minWaitDelay: range[0], maxWaitDelay: range[1] }; // WAIT_DELAY_CONSTANTS.noDelay
        },

        waitDelayModifierOptions() {
            return [
                { id: WAIT_DELAY_MODIFIER_CONSTANTS.BEFORE, label: this.$t('funnel.edit.timer.waitDelayModifierType.Before') },
                { id: WAIT_DELAY_MODIFIER_CONSTANTS.AFTER, label: this.$t('funnel.edit.timer.waitDelayModifierType.After') },
            ];
        },

        yearOptions() {
            return [
                { id: YEAR_CONSTANTS.NEXT, label: this.$t('funnel.edit.timer.dateFieldYearType.Next') },
                { id: YEAR_CONSTANTS.INFIELD, label: this.$t('funnel.edit.timer.dateFieldYearType.InField') },
            ];
        },

        timeOfDayOptions() {
            return [
                { value: this.timeDayTypesCons.AT, label: this.$t('funnel.edit.timer.timeOfDay.at') },
                { value: this.timeDayTypesCons.ANY, label: this.$t('funnel.edit.timer.timeOfDay.anyTime') },
            ];
        },

        waitDelayOptions() {
            return [
                { id: WAIT_DELAY_CONSTANTS.MINUTE, label: this.$t('funnel.edit.timer.waitDelayType.Minute'), onlyForDateTime: true },
                { id: WAIT_DELAY_CONSTANTS.HOUR, label: this.$t('funnel.edit.timer.waitDelayType.Hour'), onlyForDateTime: true },
                { id: WAIT_DELAY_CONSTANTS.DAY, label: this.$t('funnel.edit.timer.waitDelayType.Day') },
                { id: WAIT_DELAY_CONSTANTS.WEEKDAY, label: this.$t('funnel.edit.timer.waitDelayType.WeekDay') },
                { id: WAIT_DELAY_CONSTANTS.WEEK, label: this.$t('funnel.edit.timer.waitDelayType.Week') },
                { id: WAIT_DELAY_CONSTANTS.MONTH, label: this.$t('funnel.edit.timer.waitDelayType.Month') },
                { id: WAIT_DELAY_CONSTANTS.YEAR, label: this.$t('funnel.edit.timer.waitDelayType.Year') },
                { id: WAIT_DELAY_CONSTANTS.NO_DELAY, label: this.$t('funnel.edit.timer.waitDelayType.NoDelay') },
            ];
        },

        waitDelayTypes() {
            return this.waitDelayOptions.filter((waitDelayType) => {
                if (waitDelayType.onlyForDateTime && !this.isDateTimeCustomField) return false;

                return true;
            });
        },

        summaryText() {
            const waitDelayLabel = this.waitDelayOptions.find((type) => type.id === this.waitDelayType)?.label?.replace('(s)', this.waitDelay === 1 ? '' : 's');

            const waitDelayModifierTypeLabel = this.waitDelayModifierOptions.find((type) => type.id === this.waitDelayModifierType)?.label;
            const contactFieldLabel = this.contactFields.find((item) => item.value === this.contactField)?.label;
            const timezone = this.useContactTimeZone ? ` ${this.$t('funnel.edit.timer.summary.contactTimeZone')}` : this.appInfo.appTimeZoneId;
            const displayTime = this.timeOfDayType === this.timeDayTypesCons.ANY
                ? this.$t('funnel.edit.timer.timeOfDay.anyTime').toLocaleLowerCase()
                : `${this.startHour.toString().length < 2 ? `0${this.startHour}` : this.startHour}:${this.startMinute.toString().length < 2 ? `0${this.startMinute}` : this.startMinute} ${this.startAmPm}`;

            let summaryText = '';

            summaryText += this.$t('funnel.edit.timer.summary.waitUntil'); // Wait until

            if (!this.hasNoDelay) {
                summaryText += ` <b>${this.waitDelay}`; // 3
                summaryText += ` ${waitDelayLabel}`.toLocaleLowerCase(); // days
                summaryText += ` ${waitDelayModifierTypeLabel}</b>`.toLocaleLowerCase(); // before
            }
            summaryText += ` ${this.$t('funnel.edit.timer.summary.theContacts')}`; // the contact's
            if (this.yearType === YEAR_CONSTANTS.NEXT) summaryText += ` ${this.$t('funnel.edit.timer.summary.next')}`; // next
            if (contactFieldLabel) summaryText += ` "${contactFieldLabel}"`; // "Birthday"
            summaryText += '<b>';
            const timePrefixLabel = ` ${this.$t('funnel.edit.timer.timeOfDay.at')}`;

            summaryText += ` ${timePrefixLabel.toLocaleLowerCase()}`; // at
            summaryText += ` ${displayTime}`; // 8:00 AM
            summaryText += ` ${timezone}`; // (GMT -07:00) Phoenix
            summaryText += '</b>';

            return summaryText;
        },

        summaryAsCellLabel() {
            return this.summaryText.replaceAll('<b>', '').replaceAll('</b>', '');
        },

        isDateTimeCustomField() {
            return !!this.dateTimeContactFields.find((item) => item.value === this.contactField);
        },

        hasNoDelay() {
            return this.waitDelayType === WAIT_DELAY_CONSTANTS.NO_DELAY;
        },
    },

    watch: {
        contactField: {
            handler() {
                const validWaitDelayOptions = this.waitDelayTypes.map((waitDelayType) => waitDelayType.id);

                if (!validWaitDelayOptions.includes(this.waitDelayType)) {
                    this.waitDelayType = WAIT_DELAY_CONSTANTS.DAY;
                }
            },
        },
        waitDelayType: {
            handler(waitDelayRange) {
                const minRange = WAIT_DELAY_RANGES[waitDelayRange][0];
                const maxRange = WAIT_DELAY_RANGES[waitDelayRange][1];

                if (this.waitDelay > maxRange) this.waitDelay = maxRange;
                if (this.waitDelay < minRange) this.waitDelay = minRange;
            },
        },
    },

    mounted() {
        this.loadContactFields();
        this.isMounted = true;
    },

    methods: {
        closeModal() {
            this.$emit('close-modal');
        },

        async loadContactFields() {
            this.contactFields = await loadDateAndDateTimeFieldMap();
            this.dateTimeContactFields = await loadDateTimeFieldMap();
            this.contactField = this.cell?.value?.contactField ? this.cell?.value?.contactField : this.contactFields.at(0).value;
        },

        updateConfig() {
            if (this.validateConfig()) {
                const cell = { ...this.cell };

                cell.value = cell.value || {};

                cell.value.waitDelay = this.waitDelay;
                cell.value.waitDelayType = this.waitDelayType;
                cell.value.waitDelayModifierType = this.waitDelayModifierType;
                cell.value.contactField = this.contactField;
                cell.value.yearType = this.yearType;
                cell.value.startHour = this.startHour;
                cell.value.startMinute = this.startMinute;
                cell.value.startAmPm = this.startAmPm;
                cell.value.useContactTimeZone = this.useContactTimeZone ? '1' : '0';
                cell.value.timeOfDayType = this.timeOfDayType;
                cell.value.name = this.summaryAsCellLabel.replace(/(.{1,25})(\s|$)/g, '$1\n');
                cleanUpTimeFields(cell);

                this.$emit('update-cell-value', { cell });
            }
        },

        validateConfig() {
            const incomingCells = findIncomingCells(this.cell.id, this.cells);
            const outgoingCells = findOutgoingCells(this.cell.id, this.cells);

            const prevTimer = incomingCells.find((cell) => cell.value.ready === '1' && Object.values(TIMERS).includes(cell.type));
            const nextTimer = outgoingCells.find((cell) => cell.value.ready === '1' && Object.values(TIMERS).includes(cell.type));

            const startTime = createTime(this.startHour?.toString(), this.startMinute?.toString(), this.startAmPm?.toString());
            const nextStartTime = nextTimer ? createTime(nextTimer.value.startHour?.toString(), nextTimer.value.startMinute?.toString(), nextTimer.value.startAmPm?.toString()) : null;

            if (this.invalidStartTime) {
                return false;
            }

            if (prevTimer) {
                if (prevTimer.value.contactField !== this.contactField) {
                    const label = this.$t('funnel.edit.timer.error.errorMsgContactField');
                    const fieldsToCompare = `(${prevTimer.value.contactField} -> ${this.contactField})`;

                    this.validationErrorMessages = `${label} ${fieldsToCompare}`;

                    return false;
                }

                if (isBeforeOrEqualToPrevious(this.waitDelay?.toString(), this.waitDelayType, this.waitDelayModifierType, startTime, prevTimer)) {
                    this.validationErrorMessages = this.$t('funnel.edit.timer.error.errorMsgAfter');

                    return false;
                }
            }

            if (nextTimer) {
                if (nextTimer.value.contactField !== this.contactField) {
                    const label = this.$t('funnel.edit.timer.error.errorMsgContactField');
                    const fieldsToCompare = `(${this.contactField} -> ${nextTimer.value.contactField})`;

                    this.validationErrorMessages = `${label} ${fieldsToCompare}`;

                    return false;
                }

                if (isAfterOrEqualToNext(
                    this.waitDelay?.toString(),
                    this.waitDelayType,
                    this.waitDelayModifierType,
                    startTime,
                    nextTimer?.value.waitDelay?.toString(),
                    nextTimer?.value.waitDelayType,
                    nextTimer?.value.waitDelayModifierType,
                    nextStartTime,
                )) {
                    this.validationErrorMessages = this.$t('funnel.edit.timer.error.errorMsgBefore');

                    return false;
                }
            }

            return true;
        },
    },
};
</script>

<style lang="scss" scoped>
section.summary {
    padding: 0 $spacing-050;
}

.field-timer-formLabel {
    font-size: $font-size-md;
}

.formSpacer {
    padding: $spacing-025;
    display: inline-block;
    max-width: 500px;
}

#timerConfig label {
    vertical-align: baseline;
}

#timerConfig {
    height: 150px;
    width: 300px;
    white-space: nowrap;
    overflow-x: auto;
}

.top-border {
    align-items: center;
    border-top: 1px solid $color-gray-400;
    padding-top: $spacing-125;
}

.top-border-triple-wrapper {
    display: grid;
    grid-template-columns: 15% 30% 20%;
    grid-gap: 1rem;
    align-items: center;
    padding-top: $spacing-125;
}

.top-border-double-wrapper {
    display: grid;
    grid-template-columns: 40% 40%;
    grid-gap: 1rem;
    align-items: center;
    padding-top: $spacing-125;
}

.modal-content-wrapper{
    min-width: 700px;
}

.left-border {
    border-left: 1px solid $color-gray-400;
}

.right-border {
    border-right: 1px solid $color-gray-400;
}

.disabled-option {
    color: #AAAAAA;
}

.timer-option {
    vertical-align: top;
}

#timeOfDay_start .timer-option select,
#timeOfDay_stop .timer-option select{
    width: 55px;
}

.timer-parameter {
    font-weight: 600;
}

.timer-time-zone {
    color: $color-blue;
    font-weight: 600;
}

#timer-time-zone-padded-box {
    padding-left: $spacing-125;
    padding-right: $spacing-125;
}

#timer-time-zone-details {
    padding-top: 2px;
    white-space: nowrap;
}

#timer-time-zone-example-header {
    padding-top: $spacing-200;
}

.errorMessage {
    color: $color-red;
    padding-bottom: $spacing-050;
}

.small-date-picker {
    max-width: 125px;
}

.time-day-type {
    margin-bottom: $spacing-100;
}

.timer-horizontal-radio {
    display: flex;
}

#timerConfig .infusion-calendar-wrapper {
    display: contents;
}

@media only screen and (max-width: 600px) {
    #timerConfig {
        display: block;
        width: 100%
    }
}
@media only screen and (min-width: 601px) {
    #timerConfig {
        min-width: 600px;
    }
}
</style>
